import React, { useMemo, useCallback, useRef, useState, ReactNode, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { addYears } from 'date-fns';

import { KidsProfileFormProps, KidsProfileFormData } from './profile-form.models';
import { Form, IFormImperativeHandleProps } from '../../../../../common/form';
import { TextField } from '../../../../../common/form/text-field';
import { Button } from '../../../../../common/button-new';
import { useStyles } from './profile-form.styles';
import { Birthday } from '../birthday';
import { BIRTHDAY_DATE_FORMAT } from '../../../../../../constants';
import { DeleteKidsProfile } from '../../delete-kids-profile/delete-kids-profile';

export const ProfileForm = ({
  onSubmit, validationSchema, profile, disableLeavePrompt = false,
}: KidsProfileFormProps): JSX.Element => {
  const classes = useStyles();
  const formRef = useRef<IFormImperativeHandleProps>(null);
  const [disabled, setDisabled] = useState<boolean>(true);
  const isFormValidRef = useRef<boolean>(false);
  const isLeavePrompt = !disableLeavePrompt;
  const { firstName, lastName, dateOfBirth, id } = profile;
  const intl = useIntl();
  // @ts-ignore
  const date = addYears(new Date(dateOfBirth), 18).toLocaleDateString(intl.locale, BIRTHDAY_DATE_FORMAT);

  const defaultValues = useMemo(() => ({
    firstName: '',
    lastName: '',
  }), []);

  const getCurrentValues = (): KidsProfileFormData | undefined => {
    return formRef.current?.getValues() as KidsProfileFormData | undefined;
  };

  const getHasChanges = (currentFirstName?: string, currentLastName?: string): boolean => {
    return currentFirstName !== firstName || currentLastName !== lastName;
  };

  const updateSubmitState = (values?: Partial<KidsProfileFormData>): void => {
    const currentValues = getCurrentValues();
    const currentFirstName = values?.firstName?.trim() ?? currentValues?.firstName?.trim();
    const currentLastName = values?.lastName?.trim() ?? currentValues?.lastName?.trim();
    const hasChanges = getHasChanges(currentFirstName, currentLastName);

    setDisabled(!hasChanges || !isFormValidRef.current);
  };

  const handleValidateDependencies = (isValid: boolean): void => {
    isFormValidRef.current = isValid;
    updateSubmitState();
  };

  const handleChangeDependencies = (values?: Partial<KidsProfileFormData>): void => {
    updateSubmitState(values);
  };

  const handleSubmit = useCallback((values: KidsProfileFormData) => {
    onSubmit({ ...values } as KidsProfileFormData);
  }, [onSubmit]);

  useEffect(() => {
    formRef?.current?.setValue('firstName', firstName || '', { shouldDirty: false });
    formRef?.current?.setValue('lastName', lastName || '', { shouldDirty: false });
  }, [firstName, lastName]);

  return (
    <Form
      onSubmit={handleSubmit}
      withLeavePrompt={isLeavePrompt}
      validationSchema={validationSchema}
      defaultValues={defaultValues}
      ref={formRef}
      onValidateDependencies={handleValidateDependencies}
      onChangeValuesDependencies={handleChangeDependencies}
      validationMode="all"
    >
      {profile?.upcomingDeactivationDate && (
        <div className={classes.info}>
          <h3 className={classes.title}>
            <FormattedMessage id="viewKidsProfile.disclaimer.title" />
          </h3>
          <ul className={classes.list}>
            <li>
              {intl.formatMessage(
                { id: 'viewKidsProfile.disclaimer.list-1' },
                { noBreak: <>&nbsp;</>, sup: (chunk: ReactNode) => <sup>{chunk}</sup> },
              )}
            </li>
            <li>
              <FormattedMessage
                id="viewKidsProfile.disclaimer.list-2"
                values={{ date, noBreak: <>&nbsp;</> }}
              />
            </li>
          </ul>
        </div>
      )}
      <TextField
        name="firstName"
        fullWidth
        label={<FormattedMessage id="profile.nameTextField.label" />}
        isRequired
        autoComplete="off"
      />
      <TextField
        name="lastName"
        fullWidth
        label={<FormattedMessage id="profile.surnameTextField.label" />}
        isRequired
        autoComplete="off"
      />
      <Birthday date={dateOfBirth} />
      <div className={classes.actions}>
        <Button type="submit" style="dark" data-testid="profile-submit" disabled={disabled}>
          <FormattedMessage id="common.save" />
        </Button>
        <DeleteKidsProfile id={id} />
      </div>
    </Form>
  );
};
